import PersonOutlinedIcon from "@mui/icons-material/PersonOutline"
import Chip from "@mui/material/Chip"
import Typography from "@mui/material/Typography"
import { useTheme } from "@mui/material/styles"
import { styled } from "@mui/material/styles"
import { useContext, useState } from "react"
import { Position } from "reactflow"

import Column from "../../../../../../common/components/Column"
import Row from "../../../../../../common/components/Row"
import { formatDate } from "../../../../../../common/utils"
import CustomHandle from "../../CustomHandle"
import { TypedCandidatePropertiesContext } from "../../MultiConditionEditor"
import { getEditorData } from "../../utils"
import { NodeContainer, NodeContentLayout, PurpleNodeLabel } from "../BaseNode"
import EntryConditionEditor from "./Editor"

export default function EnterConditionNode(node) {
  const [editorOpen, setEditorOpen] = useState(false)
  const data = node.data
  const theme = useTheme()
  const typedCandidateProperties = useContext(TypedCandidatePropertiesContext)
  const editorData = getEditorData(node)

  return (
    <>
      <NodeContainer
        sx={{ borderColor: theme.palette.colors.brand400 }}
        onClick={() => setEditorOpen(true)}
      >
        <PurpleNodeLabel label="Trigger" />
        <NodeContentLayout
          title={data.label}
          subtitle="Entry condition"
          Icon={() => (
            <IconColumn>
              <PersonOutlinedIcon sx={{ height: "16px" }} />
            </IconColumn>
          )}
        >
          {editorData && editorData.conditionFieldsState[0].inputValue ? (
            <Column>
              <Typography
                variant="body1"
                sx={{ color: theme.palette.colors.gray600, fontSize: "9.5px" }}
              >
                Candidate must match{" "}
                <Typography
                  variant="strong-inline"
                  component="span"
                  style={{ textTransform: "uppercase" }}
                >
                  {editorData.operator}
                </Typography>{" "}
                of the following:
              </Typography>
              <ConditionColumn>
                {editorData.conditionFieldsState
                  .slice(0, 3)
                  .map((conditionField, i) => (
                    <Row key={i}>
                      <NumberChip label={i + 1} />
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: "9.5px",
                          color: theme.palette.colors.gray600,
                          ml: 0.5,
                        }}
                      >
                        {conditionField.value.display_name}{" "}
                        <Typography variant="strong-inline" component="span">
                          is {comparisonDisplay(conditionField)}
                        </Typography>{" "}
                        {comparisonTargetDisplay(
                          conditionField,
                          typedCandidateProperties
                        )}
                      </Typography>
                    </Row>
                  ))}
              </ConditionColumn>
            </Column>
          ) : null}
        </NodeContentLayout>
        <CustomHandle position={Position.Bottom} type="source" node={node} />
      </NodeContainer>
      {/* This needs to be outside of NodeContainer so clicks on the editor doesn't count as clicks on NodeContainer */}
      <EntryConditionEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        node={node}
      />
    </>
  )
}

const IconColumn = styled(Column)(({ theme }) => ({
  justifyContent: "center",
  alignItems: "center",
  width: "26px",
  height: "26px",
  border: `1px solid ${theme.palette.colors.gray300}`,
  borderRadius: "6px",
}))

const ConditionColumn = styled(Column)(({ theme }) => ({
  borderRadius: "4.5px",
  backgroundColor: theme.palette.colors.gray50,
  padding: theme.spacing(1),
  marginTop: theme.spacing(1),
  gap: theme.spacing(0.5),
}))

const NumberChip = styled(Chip)(({ theme }) => ({
  border: `0.75px solid ${theme.palette.colors.gray200}`,
  borderRadius: "3px",
  fontSize: "7.5px",
  fontWeight: 500,
  color: theme.palette.colors.gray900,
  width: "12.5px",
  height: "12.5px",
  backgroundColor: "white",
  lineHeight: 1,
  "& .MuiChip-label": {
    padding: 0,
  },
}))

function comparisonDisplay(conditionField) {
  const displayName = conditionField.comparison.display_name
  return displayName.includes("Equal") ? "equal to" : displayName.toLowerCase()
}

function comparisonTargetDisplay(conditionField, typedCandidateProperties) {
  const comparisonTarget = conditionField.comparisonTarget
  const CRM_STAGE_FIELD_NAME = "crm_stage"
  switch (conditionField.value.id) {
    case "stamp":
      return formatDate(comparisonTarget)
    case CRM_STAGE_FIELD_NAME:
      const foreignKeyFields = typedCandidateProperties.ForeignKey.fields
      if (foreignKeyFields.length === 0) {
        return "Unknown"
      }
      const options = foreignKeyFields.filter(
        (field) => field.name === CRM_STAGE_FIELD_NAME
      )[0].choices
      if (options.length === 0) {
        return comparisonTarget
      }
      return (
        options.find((crmStage) => crmStage.id === comparisonTarget)?.name ||
        "Unknown (possibly deleted)"
      )
    default:
      return comparisonTarget
  }
}
